import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Scale,
  BookOpen,
  Briefcase,
  ShieldCheck,
  ShoppingBag,
  MessageCircle,
  Send,
  Loader2,
  Sparkles,
  AlertTriangle,
  ChevronRight,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import AnswerCard from "../components/AnswerCard";

import { CATEGORIES } from "../lib/categories";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { BookOpen, Briefcase, ShieldCheck, ShoppingBag };

const SESSION_KEY = "perguntedr_session_id";

function getOrCreateSession() {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) {
    s = (crypto?.randomUUID && crypto.randomUUID()) || `s_${Date.now()}`;
    localStorage.setItem(SESSION_KEY, s);
  }
  return s;
}

export default function Home() {
  const [sessionId] = useState(() => getOrCreateSession());
  const [config, setConfig] = useState({ whatsapp_link: "#", lawyer_name: "" });
  const [activeArea, setActiveArea] = useState("geral");
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]); // {id, question, answer, area}
  const askSectionRef = useRef(null);
  const feedRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setConfig(r.data)).catch(() => {});
    axios.get(`${API}/history/${sessionId}`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [sessionId]);

  const scrollToAsk = () => {
    askSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleChip = (areaKey, chipText) => {
    setActiveArea(areaKey);
    setQuestion(chipText);
    setTimeout(scrollToAsk, 50);
  };

  const handleSelectArea = (areaKey) => {
    setActiveArea(areaKey);
    setTimeout(scrollToAsk, 50);
  };

  const submit = async () => {
    const q = question.trim();
    if (q.length < 5) {
      toast.error("Descreva sua dúvida com mais detalhes.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ask`, {
        session_id: sessionId,
        area: activeArea,
        question: q,
      });
      setItems((prev) => [...prev, data]);
      setQuestion("");
      setTimeout(() => {
        feedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Não foi possível consultar o Dr. agora.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const areaLabel = useMemo(() => {
    const m = { civil: "Direito Civil", trabalhista: "Direito Trabalhista", previdenciario: "Direito Previdenciário", consumidor: "Direito do Consumidor", geral: "Selecionar área" };
    return m[activeArea] || "Selecionar área";
  }, [activeArea]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-border"
        data-testid="hero-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1623197532650-bacb8a68914e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxjbGVhbiUyMHdoaXRlJTIwbWFyYmxlJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzc0MTcyODh8MA&ixlib=rb-4.1.0&q=85')",
          }}
        />
        <div className="absolute inset-0 paper-grain opacity-50" />
        <div className="absolute inset-0 bg-background/70" />

        <div className="relative max-w-5xl mx-auto px-6 md:px-10 pt-16 pb-20 md:pt-24 md:pb-28 text-center">
          <div className="flex items-center justify-center gap-2 text-accent uppercase tracking-[0.32em] text-xs font-semibold mb-6 animate-fade-up">
            <Sparkles className="h-3.5 w-3.5" />
            <span>Orientação jurídica inteligente</span>
          </div>

          <div
            className="mx-auto mb-6 inline-flex items-center justify-center rounded-full border border-border bg-white/70 backdrop-blur p-5 shadow-sm animate-fade-up"
            style={{ animationDelay: "60ms" }}
            data-testid="justice-scale-icon"
          >
            <Scale className="h-12 w-12 md:h-14 md:w-14 text-primary" strokeWidth={1.4} />
          </div>

          <h1
            className="font-serif text-5xl md:text-7xl font-semibold text-primary tracking-tight animate-fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="app-title"
          >
            Pergunte ao <span className="italic text-accent">Dr.</span>
          </h1>
          <p
            className="mt-5 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground animate-fade-up"
            style={{ animationDelay: "180ms" }}
          >
            Tire suas dúvidas em <strong className="text-primary">Direito Civil</strong>,{" "}
            <strong className="text-primary">Trabalhista</strong>,{" "}
            <strong className="text-primary">Previdenciário</strong> e{" "}
            <strong className="text-primary">do Consumidor</strong>. Respostas fundamentadas em
            Lei, Jurisprudência e decisões do <strong className="text-primary">STF</strong> e{" "}
            <strong className="text-primary">STJ</strong>.
          </p>

          <div
            className="mt-9 flex flex-wrap items-center justify-center gap-3 animate-fade-up"
            style={{ animationDelay: "240ms" }}
          >
            <Button
              size="lg"
              onClick={scrollToAsk}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 px-7 h-12 text-sm tracking-wide"
              data-testid="hero-cta-ask"
            >
              Fazer minha pergunta
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <a href={config.whatsapp_link} target="_blank" rel="noreferrer" data-testid="hero-cta-whatsapp">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full border-border bg-white/80 hover:bg-white h-12 px-7 text-sm tracking-wide"
              >
                <MessageCircle className="mr-2 h-4 w-4 text-[#25D366]" />
                Falar com advogado
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="categories-section">
        <div className="flex items-end justify-between gap-4 mb-10">
          <div>
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3">
              Áreas de atendimento
            </p>
            <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium">
              Escolha a sua área e vá direto ao ponto
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {CATEGORIES.map((cat, idx) => {
            const Icon = ICONS[cat.icon] || BookOpen;
            const isActive = activeArea === cat.key;
            return (
              <Card
                key={cat.key}
                className={`group bg-white border ${
                  isActive ? "border-accent shadow-md" : "border-border"
                } rounded-xl transition-all hover:-translate-y-1 hover:border-accent hover:shadow-md animate-fade-up`}
                style={{ animationDelay: `${idx * 80}ms` }}
                data-testid={`category-card-${cat.key}`}
              >
                <CardContent className="p-6 md:p-7">
                  <div className="flex items-start justify-between mb-5">
                    <div className="rounded-lg bg-secondary p-3">
                      <Icon className="h-6 w-6 text-primary" strokeWidth={1.6} />
                    </div>
                    <Badge
                      variant="outline"
                      className="text-[10px] tracking-[0.18em] uppercase border-border text-muted-foreground"
                    >
                      Especialidade
                    </Badge>
                  </div>
                  <h3 className="font-serif text-2xl text-primary font-semibold leading-tight">
                    {cat.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1.5 mb-5">{cat.subtitle}</p>

                  <div className="flex flex-wrap gap-2">
                    {cat.chips.map((chip) => (
                      <button
                        key={chip}
                        onClick={() => handleChip(cat.key, chip)}
                        className="text-xs md:text-[13px] text-primary bg-white border border-border hover:border-accent hover:text-accent transition-colors rounded-full px-3.5 py-1.5"
                        data-testid={`chip-${cat.key}-${cat.chips.indexOf(chip)}`}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>

                  <Button
                    onClick={() => handleSelectArea(cat.key)}
                    variant="ghost"
                    className="mt-6 px-0 text-primary hover:text-accent hover:bg-transparent"
                    data-testid={`select-area-${cat.key}`}
                  >
                    Perguntar sobre {cat.title}
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* ASK FORM */}
      <section
        ref={askSectionRef}
        className="bg-secondary/40 border-y border-border"
        data-testid="ask-section"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent mb-3">
            Sua consulta
          </p>
          <h2 className="font-serif text-3xl md:text-4xl text-primary font-medium mb-2">
            Descreva sua dúvida ao Dr.
          </h2>
          <p className="text-muted-foreground mb-6">
            Quanto mais detalhes, mais precisa será a orientação. Sua consulta atual:{" "}
            <span className="text-primary font-medium">{areaLabel}</span>
          </p>

          {/* Area pills */}
          <div className="flex flex-wrap gap-2 mb-5">
            {[
              { k: "geral", l: "Geral" },
              { k: "civil", l: "Civil" },
              { k: "trabalhista", l: "Trabalhista" },
              { k: "previdenciario", l: "Previdenciário" },
              { k: "consumidor", l: "Consumidor" },
            ].map((a) => (
              <button
                key={a.k}
                onClick={() => setActiveArea(a.k)}
                className={`rounded-full px-4 py-1.5 text-sm border transition-colors ${
                  activeArea === a.k
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-white text-primary border-border hover:border-accent hover:text-accent"
                }`}
                data-testid={`area-pill-${a.k}`}
              >
                {a.l}
              </button>
            ))}
          </div>

          <div className="rounded-2xl border border-border bg-white p-2 shadow-sm">
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ex.: Fui demitido sem justa causa após 4 anos. Quais verbas tenho direito a receber e em quanto tempo?"
              className="min-h-[140px] resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-base p-5 bg-transparent"
              data-testid="question-textarea"
            />
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 px-3 pb-3">
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5" />
                Orientação informativa — sempre confirme com um advogado.
              </p>
              <Button
                onClick={submit}
                disabled={loading}
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 h-11 px-6"
                data-testid="submit-question-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Consultando…
                  </>
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" />
                    Consultar o Dr.
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ANSWERS FEED */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-14" ref={feedRef} data-testid="answers-section">
        {items.length === 0 ? (
          <div className="text-center text-muted-foreground py-10" data-testid="empty-state">
            <Scale className="h-8 w-8 mx-auto mb-3 text-accent" />
            <p className="font-serif text-2xl text-primary">Nenhuma consulta ainda</p>
            <p className="text-sm">Suas perguntas e respostas aparecerão aqui.</p>
          </div>
        ) : (
          <div className="space-y-8">
            <p className="uppercase tracking-[0.2em] text-xs font-semibold text-accent">Histórico desta sessão</p>
            {items.map((it) => (
              <AnswerCard key={it.id} item={it} whatsappLink={config.whatsapp_link} />
            ))}
          </div>
        )}
      </section>

      {/* FOOTER */}
      <footer className="border-t border-border bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 text-sm text-muted-foreground flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Scale className="h-5 w-5 text-accent" />
            <span className="font-serif text-lg text-primary">Pergunte ao Dr.</span>
          </div>
          <p className="text-xs max-w-md">
            Este serviço oferece orientação jurídica informativa baseada em IA. Não substitui a consulta a um advogado habilitado.
          </p>
        </div>
      </footer>

      {/* WHATSAPP FAB */}
      <a
        href={config.whatsapp_link}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-5 right-5 md:bottom-7 md:right-7 z-40"
        data-testid="whatsapp-fab"
      >
        <div className="group flex items-center gap-3 bg-[#25D366] text-white rounded-full pl-4 pr-5 py-3 shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
          <MessageCircle className="h-5 w-5" />
          <span className="text-sm font-medium tracking-wide hidden sm:inline">
            Falar com {config.lawyer_name || "advogado"}
          </span>
        </div>
      </a>
    </div>
  );
}
